import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { LuChevronDown } from "react-icons/lu";

const HOURS = ["12", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11"];
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));
const PERIODS = ["AM", "PM"];

const toMinutesFromDisplayTime = (timeStr) => {
  if (!timeStr) return null;

  const twelveHourMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    let [, hour, minute, period] = twelveHourMatch;
    let hourNumber = Number(hour);

    if (period.toUpperCase() === "AM") {
      if (hourNumber === 12) hourNumber = 0;
    } else if (hourNumber !== 12) {
      hourNumber += 12;
    }

    return hourNumber * 60 + Number(minute);
  }

  const twentyFourHourMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHourMatch) {
    const [, hour, minute] = twentyFourHourMatch;
    const hourNumber = Number(hour);
    const minuteNumber = Number(minute);

    if (hourNumber < 0 || hourNumber > 23 || minuteNumber < 0 || minuteNumber > 59) {
      return null;
    }

    return hourNumber * 60 + minuteNumber;
  }

  return null;
};

const toMinutesFromSelection = (hour, minute, period) => {
  let hourNumber = Number(hour);
  if (period === "AM") {
    if (hourNumber === 12) hourNumber = 0;
  } else if (hourNumber !== 12) {
    hourNumber += 12;
  }

  return hourNumber * 60 + Number(minute);
};

const TimePicker = ({ value, onChange, placeholder = "Select time", minTime, maxTime }) => {
  const [isOpen, setIsOpen] = useState(false);

  const minAllowedMinutes = useMemo(() => toMinutesFromDisplayTime(minTime), [minTime]);
  const maxAllowedMinutes = useMemo(() => toMinutesFromDisplayTime(maxTime), [maxTime]);

  const isSelectionAllowed = useCallback(
    (hour, minute, period) => {
      const selectionMinutes = toMinutesFromSelection(hour, minute, period);
      if (minAllowedMinutes !== null && selectionMinutes < minAllowedMinutes) return false;
      if (maxAllowedMinutes !== null && selectionMinutes > maxAllowedMinutes) return false;
      return true;
    },
    [minAllowedMinutes, maxAllowedMinutes]
  );

  const getFirstAllowedSelection = useCallback(() => {
    for (const period of PERIODS) {
      for (const hour of HOURS) {
        for (const minute of MINUTES) {
          if (isSelectionAllowed(hour, minute, period)) {
            return { hour, minute, period };
          }
        }
      }
    }

    return { hour: "09", minute: "00", period: "AM" };
  }, [isSelectionAllowed]);

  // Parse time value (expected format: "HH:MM AM/PM")
  const parseTime = useCallback((timeStr) => {
    if (!timeStr) return getFirstAllowedSelection();

    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      const parsed = {
        hour: match[1].padStart(2, "0"),
        minute: match[2],
        period: match[3].toUpperCase(),
      };

      if (isSelectionAllowed(parsed.hour, parsed.minute, parsed.period)) {
        return parsed;
      }

      return getFirstAllowedSelection();
    }

    return getFirstAllowedSelection();
  }, [getFirstAllowedSelection, isSelectionAllowed]);

  const [selectedHour, setSelectedHour] = useState(() => parseTime(value).hour);
  const [selectedMinute, setSelectedMinute] = useState(() => parseTime(value).minute);
  const [selectedPeriod, setSelectedPeriod] = useState(() => parseTime(value).period);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const scrollRef = useRef(null);
  const touchStartY = useRef(0);

  const availablePeriods = PERIODS.filter((period) =>
    HOURS.some((hour) => MINUTES.some((minute) => isSelectionAllowed(hour, minute, period)))
  );

  const effectivePeriod = availablePeriods.includes(selectedPeriod)
    ? selectedPeriod
    : (availablePeriods[0] ?? "AM");

  const availableHours = HOURS.filter((hour) =>
    MINUTES.some((minute) => isSelectionAllowed(hour, minute, effectivePeriod))
  );

  const effectiveHour = availableHours.includes(selectedHour)
    ? selectedHour
    : (availableHours[0] ?? selectedHour);

  const availableMinutes = MINUTES.filter((minute) =>
    isSelectionAllowed(effectiveHour, minute, effectivePeriod)
  );

  const effectiveMinute = availableMinutes.includes(selectedMinute)
    ? selectedMinute
    : (availableMinutes[0] ?? selectedMinute);

  useEffect(() => {
    if (selectedPeriod !== effectivePeriod) {
      setSelectedPeriod(effectivePeriod);
    }

    if (selectedHour !== effectiveHour) {
      setSelectedHour(effectiveHour);
    }

    if (selectedMinute !== effectiveMinute) {
      setSelectedMinute(effectiveMinute);
    }
  }, [effectiveHour, effectiveMinute, effectivePeriod, selectedHour, selectedMinute, selectedPeriod]);

  useEffect(() => {
    if (value) {
      const parsed = parseTime(value);
      setSelectedHour(parsed.hour);
      setSelectedMinute(parsed.minute);
      setSelectedPeriod(parsed.period);
    }
  }, [value, parseTime]);

  // Scroll to selected items when popover opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const hourElement = document.querySelector(`[data-hour="${effectiveHour}"]`);
        const minuteElement = document.querySelector(`[data-minute="${effectiveMinute}"]`);
        hourElement?.scrollIntoView({ block: "center", behavior: "instant" });
        minuteElement?.scrollIntoView({ block: "center", behavior: "instant" });
      }, 50);
    }
  }, [isOpen, effectiveHour, effectiveMinute]);

  const formatDisplayTime = () => {
    if (!value) return null;
    return `${effectiveHour}:${effectiveMinute} ${effectivePeriod}`;
  };

  const handleTimeChange = (hour, minute, period) => {
    const newTime = `${hour}:${minute} ${period}`;
    onChange(newTime);
  };

  const handleHourSelect = (hour) => {
    const nextMinute = MINUTES.find((minute) => isSelectionAllowed(hour, minute, effectivePeriod));
    if (!nextMinute) return;

    setSelectedHour(hour);
    setSelectedMinute(nextMinute);
    handleTimeChange(hour, nextMinute, effectivePeriod);
  };

  const handleMinuteSelect = (minute) => {
    if (!isSelectionAllowed(effectiveHour, minute, effectivePeriod)) return;

    setSelectedMinute(minute);
    handleTimeChange(effectiveHour, minute, effectivePeriod);
  };

  const handlePeriodSelect = (period) => {
    const nextHour = HOURS.find((hour) =>
      MINUTES.some((minute) => isSelectionAllowed(hour, minute, period))
    );
    if (!nextHour) return;

    const nextMinute = MINUTES.find((minute) => isSelectionAllowed(nextHour, minute, period));
    if (!nextMinute) return;

    setSelectedPeriod(period);
    setSelectedHour(nextHour);
    setSelectedMinute(nextMinute);
    handleTimeChange(nextHour, nextMinute, period);
  };

  const displayTime = formatDisplayTime();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn(
            "relative h-11 w-full min-w-0 justify-between rounded-xl border border-slate-200 bg-slate-50/50 pr-4 pl-12 text-left text-sm font-medium text-slate-900 shadow-none transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-blue-600/10 sm:h-12 sm:text-base",
            !displayTime && "text-muted-foreground"
          )}
        >
          <div className="flex min-w-0 flex-1 items-center overflow-hidden">
            <span className="block min-w-0 truncate text-left">{displayTime || placeholder}</span>
          </div>
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-slate-500 opacity-70">
            <LuChevronDown className="h-4 w-4" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Hours Column */}
          <div className="flex flex-col border-r">
            <div className="px-3 py-2 text-center text-xs font-medium text-muted-foreground border-b bg-muted/30">
              Hour
            </div>
            <ScrollArea className="h-52" ref={hourRef}
              onWheel={(e) => {
                e.stopPropagation();
                if (scrollRef.current) scrollRef.current.scrollTop += e.deltaY;
              }}
              onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
              onTouchMove={(e) => {
                e.stopPropagation();
                if (scrollRef.current) {
                  const delta = touchStartY.current - e.touches[0].clientY;
                  scrollRef.current.scrollTop += delta;
                  touchStartY.current = e.touches[0].clientY;
                }
              }}>
              <div className="flex flex-col p-3">
                {availableHours.map((hour) => (
                  <button
                    key={hour}
                    data-hour={hour}
                    onClick={() => handleHourSelect(hour)}
                    className={cn(
                      "flex h-9 w-14 items-center justify-center rounded-md text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer",
                      effectiveHour === hour &&
                      "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                    )}
                  >
                    {hour}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Minutes Column */}
          <div className="flex flex-col border-r">
            <div className="px-3 py-2 text-center text-xs font-medium text-muted-foreground border-b bg-muted/30">
              Min
            </div>
            <ScrollArea className="h-52" ref={minuteRef}
              onWheel={(e) => {
                e.stopPropagation();
                if (scrollRef.current) scrollRef.current.scrollTop += e.deltaY;
              }}
              onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
              onTouchMove={(e) => {
                e.stopPropagation();
                if (scrollRef.current) {
                  const delta = touchStartY.current - e.touches[0].clientY;
                  scrollRef.current.scrollTop += delta;
                  touchStartY.current = e.touches[0].clientY;
                }
              }}>
              <div className="flex flex-col p-3">
                {availableMinutes.map((minute) => (
                  <button
                    key={minute}
                    data-minute={minute}
                    onClick={() => handleMinuteSelect(minute)}
                    className={cn(
                      "flex h-9 w-14 items-center justify-center rounded-md text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer",
                      effectiveMinute === minute &&
                      "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                    )}
                  >
                    {minute}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* AM/PM Column */}
          <div className="flex flex-col">
            <div className="px-3 py-2 text-center text-xs font-medium text-muted-foreground border-b bg-muted/30">
              Period
            </div>
            <div className="flex flex-col p-1">
              {availablePeriods.map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodSelect(period)}
                  className={cn(
                    "flex h-9 w-14 items-center justify-center rounded-md text-sm font-medium transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer",
                    effectivePeriod === period &&
                    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  )}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimePicker;
